import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Clock, CheckCircle, ChevronRight, Car, User, Pencil,
  Plus, Send, IdCard, Eye, RefreshCw, ExternalLink, FileText, DollarSign, Download, Handshake,
} from 'lucide-react';
import { US_STATES } from '@/constants/usStates';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import useAttentionSeen from '@/hooks/useAttentionSeen';
import LeadDetailsContent, {
  getLeadTypeLabel,
  getLeadStatusColor,
  formatLeadStatusLabel,
  SensitiveNpiField,
  TradeInVehicleFields,
  LEAD_CONVERTED_EDIT_DENIED_MESSAGE,
} from '@/components/admin/LeadDetailsContent';
import LeadEditModal from '@/components/admin/LeadEditModal';
import { generateLeadApplicationPdf } from '@/utils/leadApplicationPdf';
import { formatDateMdY, formatDateTimeMdY } from '@/utils/dateFormat';
import {
  CREDIT_APP_SOURCE_LABELS,
  creditAppSectionHasData,
  creditAppSupplementHasAnyData,
  creditAppSectionSource,
  creditAppHeadingSource,
  creditAppSourcesDiffer,
} from '@/utils/creditAppSupplement';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const DL_INFO_SOURCE_LABELS = {
  admin: 'Added by dealership',
  customer_request: 'Submitted by customer',
  application: 'Submitted with financing application',
};

const CREDIT_APP_SECTION_OPTIONS = [
  { key: 'landlord', label: 'Current Landlord Info' },
  { key: 'previous_residential', label: 'Previous Residential Info' },
  { key: 'previous_employment', label: 'Previous Employment Info' },
];

const OTHER_INCOME_FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

const emptyDriverLicenseForm = {
  driver_license_number: '',
  dl_issuing_county: '',
  dl_issuing_state: '',
  dl_expiry_date: '',
  date_of_birth: '',
};

const emptyCreditAppSupplementForm = {
  landlord_name: '',
  landlord_mobile: '',
  previous_street_address: '',
  previous_residence_years: '',
  previous_residence_months: '',
  previous_city: '',
  previous_state: '',
  previous_zip: '',
  previous_rent_mortgage: '',
  previous_employer: '',
  previous_job_years: '',
  previous_job_months: '',
};

const emptyOtherIncomeForm = {
  other_income_amount: '',
  other_income_frequency: '',
  source_of_other_income: '',
  reference: '',
  relationship: '',
  phone_number: '',
  address: '',
  bank_reference: '',
  bank_phone_number: '',
  bank_account_type: '',
};

const formatDate = (ds) => formatDateTimeMdY(ds);

const hasLast4 = (value) => String(value || '').replace(/\D/g, '').length >= 4;

function leadHasSsn(lead, customerInfo) {
  const src = customerInfo?.lead || lead || {};
  const details = src.details || {};
  const cobuyer = details.coBuyer?.info || {};
  return [
    src.ssn_last4,
    details.applicant?.ssn_last4,
    details.personalInfo?.ssn_last4,
    cobuyer.ssn_last4,
  ].some(hasLast4);
}

function leadHasDriverLicense(lead, customerInfo) {
  const src = customerInfo?.lead || lead || {};
  const details = src.details || {};
  const cobuyer = details.coBuyer?.info || {};
  const dlInfo = customerInfo?.driver_license_info || {};
  return [
    details.applicant?.driverLicenseLast4,
    details.personalInfo?.driverLicenseLast4,
    cobuyer.driverLicenseLast4,
    dlInfo.dl_last4,
    dlInfo.customer_driver_license_last4,
    src.driver_license_last4,
  ].some(hasLast4);
}

const vehiclePartLabel = (part) => (
  typeof part === 'string' || typeof part === 'number' ? String(part).trim() : ''
);

const vehicleDisplayLabel = (v) => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return '';
  return [v.year, v.make, v.model, v.trim].map(vehiclePartLabel).filter(Boolean).join(' ');
};

function InfoRow({ label, value, mono, highlight, className = '' }) {
  return (
    <div className={`flex justify-between items-start gap-4 ${className}`}>
      <span className="text-xs font-['Manrope'] text-white/40 shrink-0">{label}</span>
      <span className={`text-sm font-['Manrope'] text-right ${highlight ? 'text-white font-semibold' : 'text-white/80'} ${mono ? 'font-mono text-xs' : ''}`}>
        {value || '-'}
      </span>
    </div>
  );
}

function CardEditButton({ onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="absolute top-4 right-4 p-1.5 text-white/40 hover:text-white hover:bg-white/10 transition-colors"
      data-testid={testId}
      title="Edit information"
    >
      <Pencil className="w-4 h-4" />
    </button>
  );
}

function DriverLicenseInfoCard({ info, leadId, onEdit }) {
  if (!info) return null;
  return (
    <div className="relative bg-white/5 border border-white/10 p-6" data-testid="driver-license-info-card">
      {onEdit && <CardEditButton onClick={onEdit} testId="edit-driver-license-card" />}
      <div className="flex flex-wrap items-center gap-3 mb-4 pb-4 border-b border-white/10 pr-10">
        <span className="px-3 py-1 text-xs font-['Manrope'] border bg-indigo-500/10 text-indigo-400 border-indigo-500/30 flex items-center gap-1.5">
          <IdCard className="w-3.5 h-3.5" />
          Driver License Information
        </span>
        {info.source && (
          <span className="text-xs font-['Manrope'] text-white/35">
            {DL_INFO_SOURCE_LABELS[info.source] || info.source}
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SensitiveNpiField label="Driver License Number" last4={info.dl_last4} leadId={leadId} source="lead" field="driver_license" kind="dl" />
        <InfoRow label="DL Issuing County" value={info.dl_issuing_county} />
        <InfoRow label="DL Issuing State" value={info.dl_issuing_state} />
        <InfoRow label="DL Expiring Date" value={formatDateMdY(info.dl_expiry_date)} />
        <InfoRow label="Date of Birth" value={formatDateMdY(info.date_of_birth)} />
      </div>
    </div>
  );
}

const otherIncomeHasAnyData = (info) => {
  if (!info) return false;
  const keys = [
    'other_income_amount', 'other_income_frequency', 'source_of_other_income', 'reference',
    'relationship', 'phone_number', 'address', 'bank_reference', 'bank_phone_number', 'bank_account_type',
  ];
  return keys.some((key) => String(info[key] || '').trim());
};

const populateOtherIncomeFormFromInfo = (info) => ({
  other_income_amount: info?.other_income_amount || '',
  other_income_frequency: info?.other_income_frequency || '',
  source_of_other_income: info?.source_of_other_income || '',
  reference: info?.reference || '',
  relationship: info?.relationship || '',
  phone_number: info?.phone_number || '',
  address: info?.address || '',
  bank_reference: info?.bank_reference || '',
  bank_phone_number: info?.bank_phone_number || '',
  bank_account_type: info?.bank_account_type || '',
});

const validateOtherIncomeForm = (form) => {
  const required = [
    form.other_income_amount,
    form.other_income_frequency,
    form.source_of_other_income,
    form.reference,
    form.relationship,
    form.phone_number,
    form.address,
    form.bank_reference,
    form.bank_phone_number,
  ];
  if (required.some((v) => !String(v).trim())) return 'Please complete all other income fields';
  if (!form.bank_account_type) return 'Please select Checking or Saving';
  return null;
};

const buildOtherIncomePayload = (form) => ({
  other_income_amount: form.other_income_amount.trim(),
  other_income_frequency: form.other_income_frequency,
  source_of_other_income: form.source_of_other_income.trim(),
  reference: form.reference.trim(),
  relationship: form.relationship.trim(),
  phone_number: form.phone_number.trim(),
  address: form.address.trim(),
  bank_reference: form.bank_reference.trim(),
  bank_phone_number: form.bank_phone_number.trim(),
  bank_account_type: form.bank_account_type,
  mark_income_dependent: true,
});

const CreditAppSourceTag = ({ source }) => {
  if (!source) return null;
  return (
    <span className="text-xs font-['Manrope'] text-white/35">
      {CREDIT_APP_SOURCE_LABELS[source] || source}
    </span>
  );
};

function OtherIncomeFormFields({ form, setForm }) {
  const inputClass = "w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50";
  const labelClass = "block text-xs font-['Manrope'] text-white/60 mb-1.5";
  return (
    <div className="space-y-4">
      <div>
        <label className={labelClass}>Other Income Amount *</label>
        <input type="number" min="0" value={form.other_income_amount} onChange={(e) => setForm({ ...form, other_income_amount: e.target.value })} className={inputClass} />
      </div>
      <div>
        <label className={labelClass}>Other Income Frequency *</label>
        <select value={form.other_income_frequency} onChange={(e) => setForm({ ...form, other_income_frequency: e.target.value })} className={`${inputClass} [&>option]:bg-gray-900`}>
          <option value="">Select frequency...</option>
          {OTHER_INCOME_FREQUENCY_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
      </div>
      <div>
        <label className={labelClass}>Source of Other Income *</label>
        <input type="text" value={form.source_of_other_income} onChange={(e) => setForm({ ...form, source_of_other_income: e.target.value })} className={inputClass} />
      </div>
      <div>
        <label className={labelClass}>Reference *</label>
        <input type="text" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className={inputClass} />
      </div>
      <div>
        <label className={labelClass}>Relationship *</label>
        <input type="text" value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} className={inputClass} />
      </div>
      <div>
        <label className={labelClass}>Phone Number *</label>
        <input type="tel" value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} className={inputClass} />
      </div>
      <div>
        <label className={labelClass}>Address *</label>
        <input type="text" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputClass} />
      </div>
      <div>
        <label className={labelClass}>Bank Reference *</label>
        <input type="text" value={form.bank_reference} onChange={(e) => setForm({ ...form, bank_reference: e.target.value })} className={inputClass} />
      </div>
      <div>
        <label className={labelClass}>Bank Phone Number *</label>
        <input type="tel" value={form.bank_phone_number} onChange={(e) => setForm({ ...form, bank_phone_number: e.target.value })} className={inputClass} />
      </div>
      <div>
        <label className={labelClass}>Account Type *</label>
        <div className="flex gap-4 mt-1">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.bank_account_type === 'checking'} onChange={() => setForm({ ...form, bank_account_type: 'checking' })} className="accent-amber-500" />
            <span className="text-sm font-['Manrope'] text-white/80">Checking</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.bank_account_type === 'saving'} onChange={() => setForm({ ...form, bank_account_type: 'saving' })} className="accent-amber-500" />
            <span className="text-sm font-['Manrope'] text-white/80">Saving</span>
          </label>
        </div>
      </div>
    </div>
  );
}

function OtherIncomeInfoCard({ info, onEdit }) {
  if (!info || !otherIncomeHasAnyData(info)) return null;
  const frequencyLabel = OTHER_INCOME_FREQUENCY_OPTIONS.find((o) => o.value === info.other_income_frequency)?.label
    || info.other_income_frequency;
  return (
    <div className="relative bg-white/5 border border-white/10 p-6" data-testid="other-income-info-card">
      {onEdit && <CardEditButton onClick={onEdit} testId="edit-other-income-card" />}
      <div className="flex flex-wrap items-center gap-3 mb-4 pb-4 border-b border-white/10 pr-10">
        <span className="px-3 py-1 text-xs font-['Manrope'] border bg-amber-500/10 text-amber-400 border-amber-500/30 flex items-center gap-1.5">
          <DollarSign className="w-3.5 h-3.5" />
          Other Income Information
        </span>
        {info.source && (
          <span className="text-xs font-['Manrope'] text-white/35">
            {DL_INFO_SOURCE_LABELS[info.source] || info.source}
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InfoRow label="Other Income Amount" value={info.other_income_amount} />
        <InfoRow label="Frequency" value={frequencyLabel} />
        <InfoRow label="Source of Other Income" value={info.source_of_other_income} className="md:col-span-2" />
        <InfoRow label="Reference" value={info.reference} />
        <InfoRow label="Relationship" value={info.relationship} />
        <InfoRow label="Phone Number" value={info.phone_number} />
        <InfoRow label="Address" value={info.address} className="md:col-span-2" />
        <InfoRow label="Bank Reference" value={info.bank_reference} />
        <InfoRow label="Bank Phone Number" value={info.bank_phone_number} />
        <InfoRow label="Account Type" value={info.bank_account_type === 'checking' ? 'Checking' : info.bank_account_type === 'saving' ? 'Saving' : ''} />
      </div>
    </div>
  );
}

function CreditAppSupplementInfoCard({ info, onEdit }) {
  if (!info || !creditAppSupplementHasAnyData(info)) return null;
  const landlord = info.landlord || {};
  const prevRes = info.previous_residential || {};
  const prevEmp = info.previous_employment || {};
  const mixedSources = creditAppSourcesDiffer(info);
  const headingSource = creditAppHeadingSource(info);
  return (
    <div className="relative bg-white/5 border border-white/10 p-6" data-testid="credit-app-supplement-info-card">
      {onEdit && <CardEditButton onClick={onEdit} testId="edit-credit-app-card" />}
      <div className="flex flex-wrap items-center gap-3 mb-4 pb-4 border-b border-white/10 pr-10">
        <span className="px-3 py-1 text-xs font-['Manrope'] border bg-violet-500/10 text-violet-400 border-violet-500/30 flex items-center gap-1.5">
          <FileText className="w-3.5 h-3.5" />
          Credit Application — Additional Information
        </span>
        <CreditAppSourceTag source={headingSource} />
      </div>
      {creditAppSectionHasData(info, 'landlord') && (
        <div className="mb-5">
          <h4 className="text-xs font-['Unbounded'] text-white/60 mb-3 flex flex-wrap items-center gap-2">
            Current Landlord
            {mixedSources && <CreditAppSourceTag source={creditAppSectionSource(info, 'landlord')} />}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoRow label="Landlord Name" value={landlord.landlord_name} />
            <InfoRow label="Landlord Mobile" value={landlord.landlord_mobile} />
          </div>
        </div>
      )}
      {creditAppSectionHasData(info, 'previous_residential') && (
        <div className="mb-5">
          <h4 className="text-xs font-['Unbounded'] text-white/60 mb-3 flex flex-wrap items-center gap-2">
            Previous Residence
            {mixedSources && <CreditAppSourceTag source={creditAppSectionSource(info, 'previous_residential')} />}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoRow label="Street Address" value={prevRes.previous_street_address} className="md:col-span-2" />
            <InfoRow label="Years Stayed" value={prevRes.previous_residence_years} />
            <InfoRow label="Months Stayed" value={prevRes.previous_residence_months} />
            <InfoRow label="City" value={prevRes.previous_city} />
            <InfoRow label="State" value={prevRes.previous_state} />
            <InfoRow label="ZIP" value={prevRes.previous_zip} />
            <InfoRow label="Monthly Rent/Mortgage" value={prevRes.previous_rent_mortgage} />
          </div>
        </div>
      )}
      {creditAppSectionHasData(info, 'previous_employment') && (
        <div>
          <h4 className="text-xs font-['Unbounded'] text-white/60 mb-3 flex flex-wrap items-center gap-2">
            Previous Employment
            {mixedSources && <CreditAppSourceTag source={creditAppSectionSource(info, 'previous_employment')} />}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoRow label="Previous Employer" value={prevEmp.previous_employer} className="md:col-span-2" />
            <InfoRow label="Employment Years" value={prevEmp.previous_job_years} />
            <InfoRow label="Employment Months" value={prevEmp.previous_job_months} />
          </div>
        </div>
      )}
    </div>
  );
}

export default function LeadDetailPage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { token, canRevealNpi } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  useAttentionSeen('leads', token);

  const [lead, setLead] = useState(null);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('customer-information');
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [revealSsn, setRevealSsn] = useState(false);
  const [revealDl, setRevealDl] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const fetchLead = useCallback(async () => {
    try {
      const [leadRes, customerRes, timelineRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/leads/${leadId}`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/leads/${leadId}/customer-information`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/leads/${leadId}/timeline`, { headers }),
      ]);
      setLead(leadRes.data);
      setCustomerInfo(customerRes.data);
      setTimelineEvents(timelineRes.data?.events || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load lead');
      navigate('/admin/leads');
    } finally {
      setLoading(false);
    }
  }, [leadId, headers, navigate]);

  useEffect(() => { fetchLead(); }, [fetchLead]);

  const hasSsn = leadHasSsn(lead, customerInfo);
  const hasDl = leadHasDriverLicense(lead, customerInfo);

  const handleDownloadApplication = async ({ revealSsn: rSsn = false, revealDl: rDl = false } = {}) => {
    setDownloading(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/leads/${leadId}/download-application`,
        {
          reveal_ssn: Boolean(canRevealNpi && rSsn),
          reveal_driver_license: Boolean(canRevealNpi && rDl),
        },
        { headers },
      );
      generateLeadApplicationPdf(data);
      toast.success('Application downloaded');
      setShowDownloadModal(false);
      fetchLead();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download application');
    } finally {
      setDownloading(false);
    }
  };

  const openDownloadModal = () => {
    if (!leadHasSsn(lead, customerInfo) && !leadHasDriverLicense(lead, customerInfo)) {
      handleDownloadApplication();
      return;
    }
    setRevealSsn(false);
    setRevealDl(false);
    setShowDownloadModal(true);
  };

  if (loading) {
    return <div className="p-12 text-center text-white/40 font-['Manrope']">Loading lead...</div>;
  }
  if (!lead) return null;

  const TYPE_BADGE = {
    contact: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
    vehicle_inquiry: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
    financing: 'bg-green-500/10 text-green-400 border-green-500/30',
    trade_in: 'bg-orange-500/10 text-orange-400 border-orange-500/30',
    prequalification: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30',
  };
  const typeColor = getLeadStatusColor(lead.status);

  return (
    <div className="p-4 sm:p-6 max-w-[1400px] mx-auto" data-testid="lead-detail-page">
      <div className="flex items-center gap-2 mb-4 text-sm font-['Manrope']">
        <button onClick={() => navigate('/admin/leads')} className="text-white/40 hover:text-white transition-colors flex items-center gap-1" data-testid="back-to-leads">
          <ArrowLeft className="w-4 h-4" />
          Track Leads
        </button>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl font-['Unbounded'] font-bold text-white mb-1" data-testid="lead-customer-name">
            {lead.name || 'Unnamed lead'}
          </h1>
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`px-3 py-1 text-xs font-['Manrope'] font-medium border ${TYPE_BADGE[lead.type] || TYPE_BADGE.contact}`}>
              {getLeadTypeLabel(lead.type)}
            </span>
            <span className={`px-3 py-1 text-xs font-['Manrope'] font-medium border ${typeColor}`} data-testid="lead-status-badge">
              {formatLeadStatusLabel(lead.status)}
            </span>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={openDownloadModal}
            disabled={downloading}
            className="flex items-center gap-2 px-4 py-2 border border-white/10 bg-white/5 hover:bg-white/10 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50"
            data-testid="download-application-btn"
          >
            <Download className="w-4 h-4" />
            Download Application
          </button>
          {lead.is_converted && lead.deal_id && (
            <button
              type="button"
              onClick={() => navigate(`/admin/deals/${lead.deal_id}`)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold"
              data-testid="open-converted-deal-btn"
            >
              <ExternalLink className="w-4 h-4" />
              Open Deal
            </button>
          )}
        </div>
      </div>

      {lead.is_converted && (
        <div className="bg-amber-500/5 border border-amber-500/25 px-4 py-3 text-sm font-['Manrope'] text-amber-300/90 mb-6" data-testid="converted-lead-banner">
          {LEAD_CONVERTED_EDIT_DENIED_MESSAGE}
        </div>
      )}

      <div className="flex gap-0 mb-6 border-b border-white/10">
        {[
          { id: 'customer-information', label: 'Customer Information' },
          { id: 'vehicle-information', label: 'Vehicle Information' },
          { id: 'timeline', label: 'Timeline' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`tab-${tab.id}`}
            className={`px-5 py-2.5 text-sm font-['Manrope'] font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'text-blue-400 border-blue-400'
                : 'text-white/40 border-transparent hover:text-white/60'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'customer-information' && (
        <CustomerInformationTab
          customerInfo={customerInfo}
          lead={lead}
          leadId={leadId}
          token={token}
          readOnly={Boolean(lead.is_converted)}
          onRefresh={fetchLead}
        />
      )}
      {activeTab === 'vehicle-information' && (
        <VehicleInformationTab
          lead={lead}
          leadId={leadId}
          token={token}
          readOnly={Boolean(lead.is_converted)}
          onRefresh={fetchLead}
        />
      )}
      {activeTab === 'timeline' && <TimelineTab events={timelineEvents} />}

      {showDownloadModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm" data-testid="download-application-modal">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-md p-5">
            <h3 className="text-base font-['Unbounded'] font-semibold text-white mb-2">Download Application</h3>
            <p className="text-xs font-['Manrope'] text-white/50 mb-4">
              The PDF uses the current lead information, including any updates, credit-application extras, and other income.
            </p>
            <div className="space-y-3 mb-5">
              {hasSsn && (
                <label className={`flex items-start gap-3 ${canRevealNpi ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}>
                  <input
                    type="checkbox"
                    checked={revealSsn}
                    disabled={!canRevealNpi}
                    onChange={(e) => setRevealSsn(e.target.checked)}
                    className="mt-0.5 accent-amber-500"
                    data-testid="reveal-ssn-checkbox"
                  />
                  <span>
                    <span className="text-sm font-['Manrope'] text-white">Reveal SSN</span>
                    <span className="block text-[11px] font-['Manrope'] text-white/40">Print the full Social Security number instead of last 4.</span>
                  </span>
                </label>
              )}
              {hasDl && (
                <label className={`flex items-start gap-3 ${canRevealNpi ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}>
                  <input
                    type="checkbox"
                    checked={revealDl}
                    disabled={!canRevealNpi}
                    onChange={(e) => setRevealDl(e.target.checked)}
                    className="mt-0.5 accent-amber-500"
                    data-testid="reveal-dl-checkbox"
                  />
                  <span>
                    <span className="text-sm font-['Manrope'] text-white">Reveal Driver&apos;s License</span>
                    <span className="block text-[11px] font-['Manrope'] text-white/40">Print the full driver license number instead of last 4.</span>
                  </span>
                </label>
              )}
              {!canRevealNpi && (
                <p className="text-[11px] font-['Manrope'] text-amber-300/80">
                  Full SSN and driver license can only be revealed by finance or management.
                </p>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowDownloadModal(false)}
                className="px-4 py-2 border border-white/10 text-white/60 text-sm font-['Manrope']"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDownloadApplication({ revealSsn, revealDl })}
                disabled={downloading}
                data-testid="confirm-download-application-btn"
                className="px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50"
              >
                {downloading ? 'Preparing...' : 'Download PDF'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CustomerInformationTab({ customerInfo, lead, leadId, token, readOnly, onRefresh }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showRequestMenu, setShowRequestMenu] = useState(false);
  const [showAddDlModal, setShowAddDlModal] = useState(false);
  const [showRequestDlModal, setShowRequestDlModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dlForm, setDlForm] = useState(emptyDriverLicenseForm);
  const [requestEmail, setRequestEmail] = useState('');
  const [showAddCreditAppMenu, setShowAddCreditAppMenu] = useState(false);
  const [showRequestCreditAppMenu, setShowRequestCreditAppMenu] = useState(false);
  const [showAddCreditAppModal, setShowAddCreditAppModal] = useState(false);
  const [showRequestCreditAppModal, setShowRequestCreditAppModal] = useState(false);
  const [creditAppSections, setCreditAppSections] = useState({ landlord: false, previous_residential: false, previous_employment: false });
  const [creditAppForm, setCreditAppForm] = useState(emptyCreditAppSupplementForm);
  const [showAddOtherIncomeModal, setShowAddOtherIncomeModal] = useState(false);
  const [showRequestOtherIncomeModal, setShowRequestOtherIncomeModal] = useState(false);
  const [otherIncomeForm, setOtherIncomeForm] = useState(emptyOtherIncomeForm);
  const [editingLead, setEditingLead] = useState(null);

  const displayLead = customerInfo?.lead || lead;
  const driverLicenseInfo = customerInfo?.driver_license_info;
  const pendingRequest = customerInfo?.pending_driver_license_request;
  const isFinancingApplicant = Boolean(customerInfo?.is_financing_applicant || displayLead?.type === 'financing');
  const creditAppSupplement = customerInfo?.credit_app_supplement;
  const pendingCreditAppRequest = customerInfo?.pending_credit_app_supplement_request;
  const otherIncomeInfo = customerInfo?.other_income_info;
  const pendingOtherIncomeRequest = customerInfo?.pending_other_income_request;
  const canMutate = !readOnly;

  const selectedCreditAppSections = CREDIT_APP_SECTION_OPTIONS
    .filter((opt) => creditAppSections[opt.key])
    .map((opt) => opt.key);

  const resetCreditAppSections = () => setCreditAppSections({
    landlord: false,
    previous_residential: false,
    previous_employment: false,
  });

  const populateCreditAppFormFromInfo = (info) => {
    const landlord = info?.landlord || {};
    const prevRes = info?.previous_residential || {};
    const prevEmp = info?.previous_employment || {};
    setCreditAppForm({
      landlord_name: landlord.landlord_name || '',
      landlord_mobile: landlord.landlord_mobile || '',
      previous_street_address: prevRes.previous_street_address || '',
      previous_residence_years: prevRes.previous_residence_years || '',
      previous_residence_months: prevRes.previous_residence_months || '',
      previous_city: prevRes.previous_city || '',
      previous_state: prevRes.previous_state || '',
      previous_zip: prevRes.previous_zip || '',
      previous_rent_mortgage: prevRes.previous_rent_mortgage || '',
      previous_employer: prevEmp.previous_employer || '',
      previous_job_years: prevEmp.previous_job_years || '',
      previous_job_months: prevEmp.previous_job_months || '',
    });
  };

  const buildCreditAppPayload = (sections) => {
    const payload = { sections };
    if (sections.includes('landlord')) {
      payload.landlord = {
        landlord_name: creditAppForm.landlord_name.trim(),
        landlord_mobile: creditAppForm.landlord_mobile.trim(),
      };
    }
    if (sections.includes('previous_residential')) {
      payload.previous_residential = {
        previous_street_address: creditAppForm.previous_street_address.trim(),
        previous_residence_years: creditAppForm.previous_residence_years.trim(),
        previous_residence_months: creditAppForm.previous_residence_months.trim(),
        previous_city: creditAppForm.previous_city.trim(),
        previous_state: creditAppForm.previous_state.trim(),
        previous_zip: creditAppForm.previous_zip.trim(),
        previous_rent_mortgage: creditAppForm.previous_rent_mortgage.trim(),
      };
    }
    if (sections.includes('previous_employment')) {
      payload.previous_employment = {
        previous_employer: creditAppForm.previous_employer.trim(),
        previous_job_years: creditAppForm.previous_job_years.trim(),
        previous_job_months: creditAppForm.previous_job_months.trim(),
      };
    }
    return payload;
  };

  const validateCreditAppForm = (sections) => {
    if (sections.includes('landlord') && (!creditAppForm.landlord_name.trim() || !creditAppForm.landlord_mobile.trim())) {
      return 'Please complete all landlord fields';
    }
    if (sections.includes('previous_residential')) {
      const fields = [
        creditAppForm.previous_street_address,
        creditAppForm.previous_residence_years,
        creditAppForm.previous_residence_months,
        creditAppForm.previous_city,
        creditAppForm.previous_state,
        creditAppForm.previous_zip,
        creditAppForm.previous_rent_mortgage,
      ];
      if (fields.some((v) => !String(v).trim())) return 'Please complete all previous residential fields';
    }
    if (sections.includes('previous_employment')) {
      if (!creditAppForm.previous_employer.trim() || !creditAppForm.previous_job_years.trim() || !creditAppForm.previous_job_months.trim()) {
        return 'Please complete all previous employment fields';
      }
    }
    return null;
  };

  useEffect(() => {
    if (showAddDlModal && driverLicenseInfo) {
      setDlForm({
        driver_license_number: '',
        dl_issuing_county: driverLicenseInfo.dl_issuing_county || '',
        dl_issuing_state: driverLicenseInfo.dl_issuing_state || '',
        dl_expiry_date: driverLicenseInfo.dl_expiry_date || '',
        date_of_birth: driverLicenseInfo.date_of_birth || '',
      });
    }
  }, [showAddDlModal, driverLicenseInfo]);

  useEffect(() => {
    if (showRequestDlModal || showRequestCreditAppModal || showRequestOtherIncomeModal) {
      setRequestEmail(displayLead?.email || '');
    }
  }, [showRequestDlModal, showRequestCreditAppModal, showRequestOtherIncomeModal, displayLead]);

  const handleSaveDriverLicense = async () => {
    if (!dlForm.driver_license_number || !dlForm.dl_issuing_county || !dlForm.dl_issuing_state
      || !dlForm.dl_expiry_date || !dlForm.date_of_birth) {
      toast.error('Please complete all driver license fields');
      return;
    }
    setSubmitting(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/leads/${leadId}/customer-info/driver-license`, dlForm, { headers });
      toast.success('Driver license information saved');
      setShowAddDlModal(false);
      setShowAddMenu(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save driver license information');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestDriverLicense = async () => {
    if (!requestEmail.trim()) {
      toast.error('Customer email is required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/leads/${leadId}/customer-info/driver-license/request`,
        { customer_email: requestEmail.trim() },
        { headers },
      );
      toast.success('Driver license request email sent');
      setShowRequestDlModal(false);
      setShowRequestMenu(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send request email');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveCreditAppSupplement = async () => {
    if (!selectedCreditAppSections.length) {
      toast.error('Select at least one credit application section');
      return;
    }
    const validationError = validateCreditAppForm(selectedCreditAppSections);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setSubmitting(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/leads/${leadId}/customer-info/credit-application-supplement`,
        buildCreditAppPayload(selectedCreditAppSections),
        { headers },
      );
      toast.success('Credit application information saved');
      setShowAddCreditAppModal(false);
      setShowAddMenu(false);
      resetCreditAppSections();
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save credit application information');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestCreditAppSupplement = async () => {
    if (!selectedCreditAppSections.length) {
      toast.error('Select at least one credit application section');
      return;
    }
    if (!requestEmail.trim()) {
      toast.error('Customer email is required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/leads/${leadId}/customer-info/credit-application-supplement/request`,
        { customer_email: requestEmail.trim(), sections: selectedCreditAppSections },
        { headers },
      );
      toast.success('Credit application request email sent');
      setShowRequestCreditAppModal(false);
      setShowRequestMenu(false);
      resetCreditAppSections();
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send request email');
    } finally {
      setSubmitting(false);
    }
  };

  const openAddCreditAppFlow = () => {
    if (!selectedCreditAppSections.length) {
      toast.error('Select at least one section to add');
      return;
    }
    populateCreditAppFormFromInfo(creditAppSupplement);
    setShowAddCreditAppMenu(false);
    setShowAddMenu(false);
    setShowAddCreditAppModal(true);
  };

  const openRequestCreditAppFlow = () => {
    if (!selectedCreditAppSections.length) {
      toast.error('Select at least one section to request');
      return;
    }
    setShowRequestCreditAppMenu(false);
    setShowRequestMenu(false);
    setShowRequestCreditAppModal(true);
  };

  const openAddOtherIncomeFlow = () => {
    setOtherIncomeForm(populateOtherIncomeFormFromInfo(otherIncomeInfo));
    setShowAddMenu(false);
    setShowAddOtherIncomeModal(true);
  };

  const openEditCreditAppCard = () => {
    setCreditAppSections({ landlord: true, previous_residential: true, previous_employment: true });
    populateCreditAppFormFromInfo(creditAppSupplement);
    setShowAddCreditAppModal(true);
  };

  const handleSaveOtherIncome = async () => {
    const validationError = validateOtherIncomeForm(otherIncomeForm);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setSubmitting(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/leads/${leadId}/customer-info/other-income`,
        buildOtherIncomePayload(otherIncomeForm),
        { headers },
      );
      toast.success('Other income information saved');
      setShowAddOtherIncomeModal(false);
      setShowAddMenu(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save other income information');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestOtherIncome = async () => {
    if (!requestEmail.trim()) {
      toast.error('Customer email is required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/leads/${leadId}/customer-info/other-income/request`,
        { customer_email: requestEmail.trim(), mark_income_dependent: true },
        { headers },
      );
      toast.success('Other income request email sent');
      setShowRequestOtherIncomeModal(false);
      setShowRequestMenu(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send request email');
    } finally {
      setSubmitting(false);
    }
  };

  const renderCreditAppSectionCheckboxes = () => (
    <div className="mt-3 ml-2 pl-3 border-l border-violet-500/30 space-y-2" data-testid="credit-app-section-checkboxes">
      {CREDIT_APP_SECTION_OPTIONS.map((opt) => (
        <label key={opt.key} className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={Boolean(creditAppSections[opt.key])}
            onChange={(e) => setCreditAppSections((prev) => ({ ...prev, [opt.key]: e.target.checked }))}
            className="accent-violet-500"
          />
          <span className="text-sm font-['Manrope'] text-white/80">{opt.label}</span>
        </label>
      ))}
    </div>
  );

  const renderCreditAppFormFields = (sections) => (
    <div className="space-y-5">
      {sections.includes('landlord') && (
        <div className="space-y-3 border border-white/10 p-4">
          <h4 className="text-xs font-['Unbounded'] text-white/70">Current Landlord Info</h4>
          <div>
            <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Landlord Name *</label>
            <input type="text" value={creditAppForm.landlord_name} onChange={(e) => setCreditAppForm({ ...creditAppForm, landlord_name: e.target.value })} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
          </div>
          <div>
            <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Landlord Mobile Number *</label>
            <input type="tel" value={creditAppForm.landlord_mobile} onChange={(e) => setCreditAppForm({ ...creditAppForm, landlord_mobile: e.target.value })} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
          </div>
        </div>
      )}
      {sections.includes('previous_residential') && (
        <div className="space-y-3 border border-white/10 p-4">
          <h4 className="text-xs font-['Unbounded'] text-white/70">Previous Residential Info</h4>
          <div>
            <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Previous Street Address *</label>
            <input type="text" value={creditAppForm.previous_street_address} onChange={(e) => setCreditAppForm({ ...creditAppForm, previous_street_address: e.target.value })} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Years Stayed *</label>
              <input type="number" min="0" value={creditAppForm.previous_residence_years} onChange={(e) => setCreditAppForm({ ...creditAppForm, previous_residence_years: e.target.value })} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
            </div>
            <div>
              <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Months Stayed *</label>
              <input type="number" min="0" max="11" value={creditAppForm.previous_residence_months} onChange={(e) => setCreditAppForm({ ...creditAppForm, previous_residence_months: e.target.value })} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">City *</label>
              <input type="text" value={creditAppForm.previous_city} onChange={(e) => setCreditAppForm({ ...creditAppForm, previous_city: e.target.value })} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
            </div>
            <div>
              <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">State *</label>
              <select value={creditAppForm.previous_state} onChange={(e) => setCreditAppForm({ ...creditAppForm, previous_state: e.target.value })} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 [&>option]:bg-gray-900">
                <option value="">Select state...</option>
                {US_STATES.map((st) => <option key={st} value={st}>{st}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">ZIP *</label>
              <input type="text" value={creditAppForm.previous_zip} onChange={(e) => setCreditAppForm({ ...creditAppForm, previous_zip: e.target.value })} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
            </div>
            <div>
              <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Monthly Rent/Mortgage *</label>
              <input type="number" min="0" value={creditAppForm.previous_rent_mortgage} onChange={(e) => setCreditAppForm({ ...creditAppForm, previous_rent_mortgage: e.target.value })} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
            </div>
          </div>
        </div>
      )}
      {sections.includes('previous_employment') && (
        <div className="space-y-3 border border-white/10 p-4">
          <h4 className="text-xs font-['Unbounded'] text-white/70">Previous Employment Info</h4>
          <div>
            <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Previous Employer *</label>
            <input type="text" value={creditAppForm.previous_employer} onChange={(e) => setCreditAppForm({ ...creditAppForm, previous_employer: e.target.value })} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Employment Years *</label>
              <input type="number" min="0" value={creditAppForm.previous_job_years} onChange={(e) => setCreditAppForm({ ...creditAppForm, previous_job_years: e.target.value })} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
            </div>
            <div>
              <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Employment Months *</label>
              <input type="number" min="0" max="11" value={creditAppForm.previous_job_months} onChange={(e) => setCreditAppForm({ ...creditAppForm, previous_job_months: e.target.value })} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (!customerInfo) {
    return (
      <div className="p-12 text-center text-white/40 font-['Manrope']" data-testid="customer-information-tab">
        Loading customer information...
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="customer-information-tab">
      <div className="bg-white/5 border border-white/10 p-5">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
          <div>
            <h3 className="text-sm font-['Unbounded'] font-semibold text-white/80 mb-2 flex items-center gap-2">
              <User className="w-4 h-4 text-blue-400" />
              Customer Information
            </h3>
            <p className="text-lg font-['Manrope'] font-semibold text-white">{displayLead?.name || lead.name}</p>
            <p className="text-xs font-['Manrope'] text-white/40 mt-1">
              {getLeadTypeLabel(displayLead?.type || lead.type)} submitted {formatDate(displayLead?.created_at || lead.created_at)}
            </p>
          </div>
          {canMutate && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => { setShowAddMenu(true); setShowRequestMenu(false); }}
                data-testid="add-more-customer-info-btn"
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold"
              >
                <Plus className="w-4 h-4" />
                Add More Customer Info
              </button>
              <button
                type="button"
                onClick={() => { setShowRequestMenu(true); setShowAddMenu(false); }}
                data-testid="request-more-customer-info-btn"
                className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/15 text-white text-sm font-['Manrope'] font-medium border border-white/10"
              >
                <Send className="w-4 h-4" />
                Request More Customer Info
              </button>
            </div>
          )}
        </div>
      </div>

      {pendingRequest && (
        <div className="bg-amber-500/5 border border-amber-500/25 px-4 py-3 text-sm font-['Manrope'] text-amber-300/90" data-testid="pending-dl-request-banner">
          Driver license details requested — awaiting customer response at {pendingRequest.customer_email}.
        </div>
      )}
      {pendingCreditAppRequest && (
        <div className="bg-violet-500/5 border border-violet-500/25 px-4 py-3 text-sm font-['Manrope'] text-violet-300/90">
          Credit application supplemental info requested — awaiting customer response at {pendingCreditAppRequest.customer_email}
          {(pendingCreditAppRequest.requested_sections || []).length > 0 && (
            <span className="text-white/50"> ({pendingCreditAppRequest.requested_sections.join(', ').replace(/_/g, ' ')})</span>
          )}.
        </div>
      )}
      {pendingOtherIncomeRequest && (
        <div className="bg-amber-500/5 border border-amber-500/25 px-4 py-3 text-sm font-['Manrope'] text-amber-300/90">
          Other income information requested — awaiting customer response at {pendingOtherIncomeRequest.customer_email}.
        </div>
      )}

      <DriverLicenseInfoCard
        info={driverLicenseInfo}
        leadId={leadId}
        onEdit={canMutate ? () => setShowAddDlModal(true) : undefined}
      />
      {isFinancingApplicant && (
        <CreditAppSupplementInfoCard
          info={creditAppSupplement}
          onEdit={canMutate ? openEditCreditAppCard : undefined}
        />
      )}
      {isFinancingApplicant && (
        <OtherIncomeInfoCard
          info={otherIncomeInfo}
          onEdit={canMutate ? openAddOtherIncomeFlow : undefined}
        />
      )}

      <div className="relative bg-white/5 border border-white/10 p-6">
        {canMutate && <CardEditButton onClick={() => setEditingLead(displayLead)} testId="edit-lead-card" />}
        <div className="flex flex-wrap items-center gap-3 mb-5 pb-4 border-b border-white/10 pr-10">
          <span className={`px-3 py-1 text-xs font-['Manrope'] border ${getLeadStatusColor(displayLead.status)}`}>
            {getLeadTypeLabel(displayLead.type)}
          </span>
          <span className={`px-3 py-1 text-xs font-['Manrope'] border ${getLeadStatusColor(displayLead.status)}`}>
            {formatLeadStatusLabel(displayLead.status)}
          </span>
        </div>
        <h4 className="text-base font-['Unbounded'] font-semibold text-white mb-4">{displayLead.name}</h4>
        <LeadDetailsContent
          lead={displayLead}
          showVehicleInterest={false}
          showTradeInVehicle={displayLead.type === 'trade_in'}
        />
      </div>

      {showAddMenu && (
        <div className="fixed inset-0 z-[65] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="add-customer-info-menu">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-md p-5">
            <h3 className="text-base font-['Unbounded'] font-semibold text-white mb-4">Add More Customer Info</h3>
            <button
              type="button"
              onClick={() => { setShowAddMenu(false); setShowAddDlModal(true); if (!driverLicenseInfo) setDlForm(emptyDriverLicenseForm); }}
              className="w-full text-left px-4 py-3 border border-white/10 hover:bg-white/5 text-sm font-['Manrope'] text-white mb-3"
            >
              Add Driver License Related Information
            </button>
            {isFinancingApplicant && (
              <div className="mb-3 border border-violet-500/20 p-3">
                <button
                  type="button"
                  onClick={() => setShowAddCreditAppMenu((v) => !v)}
                  className="w-full text-left text-sm font-['Manrope'] text-white flex items-center justify-between"
                >
                  <span>Add Additional Information for Credit Application</span>
                  <ChevronRight className={`w-4 h-4 transition-transform ${showAddCreditAppMenu ? 'rotate-90' : ''}`} />
                </button>
                {showAddCreditAppMenu && (
                  <>
                    {renderCreditAppSectionCheckboxes()}
                    <button type="button" onClick={openAddCreditAppFlow} className="mt-3 w-full px-3 py-2 bg-violet-600 hover:bg-violet-500 text-white text-sm font-['Manrope'] font-semibold">
                      Continue to Add
                    </button>
                  </>
                )}
              </div>
            )}
            {isFinancingApplicant && (
              <button
                type="button"
                onClick={openAddOtherIncomeFlow}
                className="w-full text-left px-4 py-3 border border-amber-500/20 hover:bg-white/5 text-sm font-['Manrope'] text-white mb-3"
              >
                Add Additional Info About Other Income
              </button>
            )}
            <button type="button" onClick={() => { setShowAddMenu(false); setShowAddCreditAppMenu(false); resetCreditAppSections(); }} className="text-sm font-['Manrope'] text-white/50 hover:text-white">Cancel</button>
          </div>
        </div>
      )}

      {showRequestMenu && (
        <div className="fixed inset-0 z-[65] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="request-customer-info-menu">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-md p-5">
            <h3 className="text-base font-['Unbounded'] font-semibold text-white mb-4">Request More Customer Info</h3>
            <button
              type="button"
              onClick={() => { setShowRequestMenu(false); setShowRequestDlModal(true); }}
              className="w-full text-left px-4 py-3 border border-white/10 hover:bg-white/5 text-sm font-['Manrope'] text-white mb-3"
            >
              Request Driver License Details
            </button>
            {isFinancingApplicant && (
              <div className="mb-3 border border-violet-500/20 p-3">
                <button
                  type="button"
                  onClick={() => setShowRequestCreditAppMenu((v) => !v)}
                  className="w-full text-left text-sm font-['Manrope'] text-white flex items-center justify-between"
                >
                  <span>Request Additional Information for Credit Application</span>
                  <ChevronRight className={`w-4 h-4 transition-transform ${showRequestCreditAppMenu ? 'rotate-90' : ''}`} />
                </button>
                {showRequestCreditAppMenu && (
                  <>
                    {renderCreditAppSectionCheckboxes()}
                    <button type="button" onClick={openRequestCreditAppFlow} className="mt-3 w-full px-3 py-2 bg-violet-600 hover:bg-violet-500 text-white text-sm font-['Manrope'] font-semibold">
                      Continue to Request
                    </button>
                  </>
                )}
              </div>
            )}
            {isFinancingApplicant && (
              <button
                type="button"
                onClick={() => { setShowRequestMenu(false); setShowRequestOtherIncomeModal(true); }}
                className="w-full text-left px-4 py-3 border border-amber-500/20 hover:bg-white/5 text-sm font-['Manrope'] text-white mb-3"
              >
                Request Additional Info About Other Income
              </button>
            )}
            <button type="button" onClick={() => { setShowRequestMenu(false); setShowRequestCreditAppMenu(false); resetCreditAppSections(); }} className="text-sm font-['Manrope'] text-white/50 hover:text-white">Cancel</button>
          </div>
        </div>
      )}

      {showAddDlModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h3 className="text-base font-['Unbounded'] font-semibold text-white">Driver License Related Information</h3>
              <button type="button" onClick={() => setShowAddDlModal(false)} className="text-white/40 hover:text-white text-xl">&times;</button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Driver License Number # *</label>
                <input type="text" value={dlForm.driver_license_number} onChange={(e) => setDlForm({ ...dlForm, driver_license_number: e.target.value })} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
              </div>
              <div>
                <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">DL Issuing County *</label>
                <input type="text" value={dlForm.dl_issuing_county} onChange={(e) => setDlForm({ ...dlForm, dl_issuing_county: e.target.value })} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
              </div>
              <div>
                <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">DL Issuing State *</label>
                <select value={dlForm.dl_issuing_state} onChange={(e) => setDlForm({ ...dlForm, dl_issuing_state: e.target.value })} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 [&>option]:bg-gray-900">
                  <option value="">Select state...</option>
                  {US_STATES.map((st) => <option key={st} value={st}>{st}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">DL Expiring Date *</label>
                <input type="date" value={dlForm.dl_expiry_date} onChange={(e) => setDlForm({ ...dlForm, dl_expiry_date: e.target.value })} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
              </div>
              <div>
                <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Date Of Birth *</label>
                <input type="date" value={dlForm.date_of_birth} onChange={(e) => setDlForm({ ...dlForm, date_of_birth: e.target.value })} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-white/10">
              <button type="button" onClick={() => setShowAddDlModal(false)} className="px-4 py-2 border border-white/10 text-white/60 text-sm font-['Manrope']">Cancel</button>
              <button type="button" onClick={handleSaveDriverLicense} disabled={submitting} className="px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50">
                {submitting ? 'Saving...' : 'Save Information'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRequestDlModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-md p-5">
            <h3 className="text-base font-['Unbounded'] font-semibold text-white mb-2">Request Driver License Details</h3>
            <p className="text-xs font-['Manrope'] text-white/50 mb-4">
              An email will be sent with a secure form for the customer to submit their driver license information.
            </p>
            <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Customer Email *</label>
            <input type="email" value={requestEmail} onChange={(e) => setRequestEmail(e.target.value)} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 mb-5" />
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setShowRequestDlModal(false)} className="px-4 py-2 border border-white/10 text-white/60 text-sm font-['Manrope']">Cancel</button>
              <button type="button" onClick={handleRequestDriverLicense} disabled={submitting} className="px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50">
                {submitting ? 'Sending...' : 'Send Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddCreditAppModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h3 className="text-base font-['Unbounded'] font-semibold text-white">Credit Application — Additional Information</h3>
              <button type="button" onClick={() => setShowAddCreditAppModal(false)} className="text-white/40 hover:text-white text-xl">&times;</button>
            </div>
            <div className="p-5">{renderCreditAppFormFields(selectedCreditAppSections)}</div>
            <div className="flex justify-end gap-3 p-5 border-t border-white/10">
              <button type="button" onClick={() => setShowAddCreditAppModal(false)} className="px-4 py-2 border border-white/10 text-white/60 text-sm font-['Manrope']">Cancel</button>
              <button type="button" onClick={handleSaveCreditAppSupplement} disabled={submitting} className="px-5 py-2 bg-violet-600 hover:bg-violet-500 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50">
                {submitting ? 'Saving...' : 'Save Information'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRequestCreditAppModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-md p-5">
            <h3 className="text-base font-['Unbounded'] font-semibold text-white mb-2">Request Credit Application Information</h3>
            <ul className="text-xs font-['Manrope'] text-violet-300/90 mb-4 list-disc list-inside">
              {selectedCreditAppSections.map((key) => (
                <li key={key}>{CREDIT_APP_SECTION_OPTIONS.find((o) => o.key === key)?.label || key}</li>
              ))}
            </ul>
            <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Customer Email *</label>
            <input type="email" value={requestEmail} onChange={(e) => setRequestEmail(e.target.value)} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 mb-5" />
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setShowRequestCreditAppModal(false)} className="px-4 py-2 border border-white/10 text-white/60 text-sm font-['Manrope']">Cancel</button>
              <button type="button" onClick={handleRequestCreditAppSupplement} disabled={submitting} className="px-5 py-2 bg-violet-600 hover:bg-violet-500 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50">
                {submitting ? 'Sending...' : 'Send Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddOtherIncomeModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h3 className="text-base font-['Unbounded'] font-semibold text-white">Additional Info About Other Income</h3>
              <button type="button" onClick={() => setShowAddOtherIncomeModal(false)} className="text-white/40 hover:text-white text-xl">&times;</button>
            </div>
            <div className="p-5">
              <OtherIncomeFormFields form={otherIncomeForm} setForm={setOtherIncomeForm} />
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-white/10">
              <button type="button" onClick={() => setShowAddOtherIncomeModal(false)} className="px-4 py-2 border border-white/10 text-white/60 text-sm font-['Manrope']">Cancel</button>
              <button type="button" onClick={handleSaveOtherIncome} disabled={submitting} className="px-5 py-2 bg-amber-600 hover:bg-amber-500 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50">
                {submitting ? 'Saving...' : 'Save Information'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRequestOtherIncomeModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-md p-5">
            <h3 className="text-base font-['Unbounded'] font-semibold text-white mb-2">Request Other Income Information</h3>
            <p className="text-xs font-['Manrope'] text-white/50 mb-4">
              An email will be sent with a secure form for the customer to submit their other income details.
            </p>
            <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Customer Email *</label>
            <input type="email" value={requestEmail} onChange={(e) => setRequestEmail(e.target.value)} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 mb-5" />
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setShowRequestOtherIncomeModal(false)} className="px-4 py-2 border border-white/10 text-white/60 text-sm font-['Manrope']">Cancel</button>
              <button type="button" onClick={handleRequestOtherIncome} disabled={submitting} className="px-5 py-2 bg-amber-600 hover:bg-amber-500 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50">
                {submitting ? 'Sending...' : 'Send Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      <LeadEditModal
        isOpen={Boolean(editingLead)}
        lead={editingLead}
        token={token}
        hideStatus
        title="Edit Customer Information"
        onClose={() => setEditingLead(null)}
        onSaved={() => { setEditingLead(null); onRefresh(); }}
        onDeleted={() => navigate('/admin/leads')}
      />
    </div>
  );
}

function VehicleInformationTab({ lead, leadId, token, readOnly, onRefresh }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [vehicle, setVehicle] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const vehicleId = lead?.interested_vehicle_id || lead?.details?.vehicle_id || '';
  const [selectedId, setSelectedId] = useState(vehicleId);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const linkedId = lead?.interested_vehicle_id || lead?.details?.vehicle_id || '';
        const [vehRes, listRes] = await Promise.all([
          linkedId
            ? axios.get(`${BACKEND_URL}/api/vehicles/${linkedId}`).catch(() => ({ data: null }))
            : Promise.resolve({ data: null }),
          axios.get(`${BACKEND_URL}/api/vehicles`),
        ]);
        if (cancelled) return;
        setVehicle(vehRes.data);
        setInventory(Array.isArray(listRes.data) ? listRes.data : []);
        setSelectedId(linkedId);
      } catch (err) {
        if (!cancelled) toast.error('Failed to load vehicle information');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [lead?.interested_vehicle_id, lead?.details?.vehicle_id, token]);

  const handleLinkVehicle = async () => {
    if (!selectedId) {
      toast.error('Choose a vehicle to link');
      return;
    }
    if (selectedId === vehicleId) {
      setShowPicker(false);
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${BACKEND_URL}/api/admin/leads/${leadId}/vehicle`,
        { vehicle_id: selectedId },
        { headers },
      );
      toast.success('Vehicle interested updated');
      setVehicle(data.vehicle || inventory.find((v) => v.id === selectedId) || null);
      setShowPicker(false);
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update vehicle interested');
    } finally {
      setSaving(false);
    }
  };

  const submittedInterestText = (value) => (
    typeof value === 'string' || typeof value === 'number' ? String(value).trim() : ''
  );
  const submittedInterest = submittedInterestText(lead?.details?.vehicle_info)
    || submittedInterestText(lead?.details?.interestedVehicle)
    || submittedInterestText(lead?.details?.vehicle_interest)
    || vehicleDisplayLabel(lead?.details?.vehicle);

  if (loading) {
    return (
      <div className="p-12 text-center text-white/40 font-['Manrope']" data-testid="vehicle-information-tab">
        Loading vehicle information...
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="vehicle-information-tab">
      <div className="relative bg-white/5 border border-white/10 p-6">
        {!readOnly && <CardEditButton onClick={() => setShowPicker((v) => !v)} testId="edit-lead-vehicle-btn" />}
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 pr-10">
          <div>
            <h3 className="text-sm font-['Unbounded'] font-semibold text-white/80 mb-2 flex items-center gap-2">
              <Car className="w-4 h-4 text-purple-400" />
              Vehicle Interested
            </h3>
            <p className="text-lg font-['Manrope'] font-semibold text-white">
              {vehicleDisplayLabel(vehicle) || submittedInterest || 'No vehicle linked'}
            </p>
            {vehicle?.stockNumber && (
              <p className="text-xs font-['Manrope'] text-white/40 mt-1">Stock #{vehicle.stockNumber}</p>
            )}
          </div>
          {vehicle && (
            <div className="flex items-baseline gap-2 lg:text-right">
              <span className="text-xs font-['Manrope'] text-white/45">Listed Price</span>
              <p className="text-xl font-['Unbounded'] font-bold text-white">
                {vehicle.price_not_decided ? 'Price TBD' : `$${Number(vehicle.price || 0).toLocaleString()}`}
              </p>
            </div>
          )}
        </div>

        {showPicker && !readOnly && (
          <div className="mt-5 pt-4 border-t border-white/10 space-y-3" data-testid="lead-vehicle-picker">
            <label className="block text-xs font-['Manrope'] text-white/60">
              Choose a vehicle from inventory
            </label>
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 [&>option]:bg-gray-900"
            >
              <option value="">Select from inventory...</option>
              {inventory.map((v) => (
                <option key={v.id} value={v.id}>
                  {[v.year, v.make, v.model, v.trim].filter(Boolean).join(' ')}
                  {v.price != null ? ` — $${Number(v.price).toLocaleString()}` : ''}
                  {v.vin ? ` · ${v.vin}` : ''}
                  {v.status ? ` (${v.status})` : ''}
                </option>
              ))}
            </select>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { setShowPicker(false); setSelectedId(vehicleId); }}
                className="px-4 py-2 border border-white/10 text-white/60 text-sm font-['Manrope']"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleLinkVehicle}
                disabled={saving || !selectedId}
                className="px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50"
              >
                {saving ? 'Linking...' : 'Link Vehicle'}
              </button>
            </div>
          </div>
        )}
      </div>

      {!vehicle ? (
        <div className="bg-white/5 border border-white/10 p-8 text-center">
          <Car className="w-10 h-10 text-white/20 mx-auto mb-3" />
          <p className="text-white/60 font-['Manrope'] text-sm">
            {submittedInterest
              ? `Submitted interest: ${submittedInterest}. ${readOnly ? '' : 'Use the pencil icon to link an inventory vehicle.'}`
              : `No vehicle is linked to this lead. ${readOnly ? '' : 'Use the pencil icon to choose one from inventory.'}`}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white/5 border border-white/10 p-5">
            <h4 className="text-sm font-['Unbounded'] font-semibold text-white/80 mb-4">Details</h4>
            <div className="space-y-3">
              <InfoRow label="Year" value={vehicle.year} />
              <InfoRow label="Make" value={vehicle.make} />
              <InfoRow label="Model" value={vehicle.model} />
              <InfoRow label="Trim" value={vehicle.trim} />
              <InfoRow label="Stock Number" value={vehicle.stockNumber} />
              <InfoRow label="VIN" value={vehicle.vin} mono />
              <InfoRow label="Status" value={vehicle.status} />
              <InfoRow label="Condition" value={vehicle.condition} />
            </div>
          </div>
          <div className="bg-white/5 border border-white/10 p-5">
            <h4 className="text-sm font-['Unbounded'] font-semibold text-white/80 mb-4">Specs</h4>
            <div className="space-y-3">
              <InfoRow label="Mileage" value={vehicle.mileage != null ? `${Number(vehicle.mileage).toLocaleString()} mi` : ''} />
              <InfoRow label="Price" value={vehicle.price_not_decided ? 'Price TBD' : (vehicle.price != null ? `$${Number(vehicle.price).toLocaleString()}` : '')} highlight />
              <InfoRow label="Exterior Color" value={vehicle.exterior_color} />
              <InfoRow label="Interior Color" value={vehicle.interior_color} />
              <InfoRow label="Fuel Type" value={vehicle.fuel_type} />
              <InfoRow label="Transmission" value={vehicle.transmission} />
              <InfoRow label="Engine" value={vehicle.engine} />
              <InfoRow label="Body Type" value={vehicle.body_type} />
              <InfoRow label="Drivetrain" value={vehicle.drivetrain} />
              <InfoRow label="Cylinders" value={vehicle.cylinders} />
              <InfoRow label="City MPG" value={vehicle.city_mpg} />
              <InfoRow label="Highway MPG" value={vehicle.hwy_mpg} />
            </div>
          </div>
          {Array.isArray(vehicle.features) && vehicle.features.length > 0 && (
            <div className="bg-white/5 border border-white/10 p-5 lg:col-span-2">
              <h4 className="text-sm font-['Unbounded'] font-semibold text-white/80 mb-3">Features</h4>
              <div className="flex flex-wrap gap-2">
                {vehicle.features.map((f) => (
                  <span key={f} className="px-2.5 py-1 text-[11px] font-['Manrope'] bg-white/5 border border-white/10 text-white/70">
                    {f}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {lead.type === 'trade_in' && (lead.details?.vehicleInfo || lead.details?.vehicle || lead.details?.description) && (
        <div className="bg-white/5 border border-white/10 p-6" data-testid="trade-in-vehicle-card">
          <h3 className="text-sm font-['Unbounded'] font-semibold text-white/80 mb-1 flex items-center gap-2">
            <Car className="w-4 h-4 text-orange-400" />
            Trade-In Vehicle
          </h3>
          <p className="text-xs font-['Manrope'] text-white/40 mb-5">
            Vehicle the customer wants to trade in
          </p>
          {(lead.details?.vehicleInfo || lead.details?.vehicle) && (
            <TradeInVehicleFields lead={lead} />
          )}
          {lead.details?.description && (
            <div className={(lead.details?.vehicleInfo || lead.details?.vehicle) ? 'mt-5 pt-5 border-t border-white/10' : ''}>
              <label className="text-sm font-['Manrope'] text-white/60 mb-1 block">Description</label>
              <p className="text-white font-['Manrope'] bg-white/5 p-3 border border-white/10">
                {lead.details.description}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const TIMELINE_EVENT_PRESENTATION = {
  lead_submitted: { label: 'Lead Submitted', icon: Clock, color: 'text-blue-400' },
  lead_updated: { label: 'Lead Information Updated', icon: Pencil, color: 'text-sky-400' },
  lead_status_changed: { label: 'Lead Status Changed', icon: RefreshCw, color: 'text-purple-400' },
  lead_vehicle_changed: { label: 'Vehicle Interested Changed', icon: Car, color: 'text-purple-400' },
  customer_info_updated: { label: 'Customer Information Updated', icon: Pencil, color: 'text-sky-400' },
  customer_info_requested: { label: 'Customer Information Requested', icon: Send, color: 'text-amber-400' },
  customer_info_received: { label: 'Customer Information Received', icon: CheckCircle, color: 'text-emerald-400' },
  pii_revealed: { label: 'SSN / License Revealed', icon: Eye, color: 'text-amber-400' },
  application_downloaded: { label: 'Application Downloaded', icon: Download, color: 'text-cyan-400' },
  lead_converted_to_deal: { label: 'Lead Converted to a Deal', icon: Handshake, color: 'text-green-400' },
};

const formatTimelineActor = (event) => {
  const actor = event.actor || event.metadata?.actor || {};
  const name = event.actor_name || actor.name || '';
  const email = event.actor_email || actor.email || '';
  if (name && email && name !== email) return `${name} · ${email}`;
  return name || email || '';
};

function TimelineTab({ events }) {
  const displayEvents = (events || [])
    .map((event) => {
      const preset = TIMELINE_EVENT_PRESENTATION[event.event_type] || {
        label: (event.event_type || 'Event').replace(/_/g, ' '),
        icon: Clock,
        color: 'text-white/50',
      };
      return {
        id: event.id,
        date: event.occurred_at,
        label: preset.label,
        desc: event.description || '',
        actor: formatTimelineActor(event),
        icon: preset.icon,
        color: preset.color,
      };
    })
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div data-testid="timeline-tab">
      {displayEvents.length === 0 ? (
        <div className="bg-white/5 border border-white/10 p-12 text-center">
          <p className="text-white/40 font-['Manrope'] text-sm">No timeline events.</p>
        </div>
      ) : (
        <div className="space-y-0">
          {displayEvents.map((ev, i) => {
            const Icon = ev.icon;
            return (
              <div key={ev.id || i} className="flex gap-4 relative" data-testid={`timeline-event-${ev.id?.slice(0, 8) || i}`}>
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-white/5 border border-white/10 shrink-0">
                    <Icon className={`w-4 h-4 ${ev.color}`} />
                  </div>
                  {i < displayEvents.length - 1 && <div className="w-px flex-1 bg-white/10 my-1" />}
                </div>
                <div className="pb-6">
                  <p className="text-sm font-['Manrope'] font-medium text-white">{ev.label}</p>
                  <p className="text-xs font-['Manrope'] text-white/40">{ev.desc}</p>
                  {ev.actor && (
                    <p className="text-[11px] font-['Manrope'] text-white/35 mt-0.5">By {ev.actor}</p>
                  )}
                  <p className="text-[11px] font-['Manrope'] text-white/25 mt-0.5">{formatDate(ev.date)}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
